import React, { useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useFormik } from "formik";

import SVG from "@/assets/svg";
import DefaultLoader from "@/component/loader";
import AdditionalRecordedMetadata from "@/component/care-planning/AdditionalRecordedMetadata";
import CostumeButton from "@/component/button";
import TemplateOrigin from "@/component/task-template/TemplateOrigin";
import { successToast } from "@/component/toasts";
import {
  IDEAL_TIME,
  TaskManagement,
  TaskManagementResponse,
} from "@/interfaces/ITaskManagement";
import InfoSection from "@/pages/tasks-management/components/InfoSection";
import { getTaskById, updateTask } from "@/services/task-management";
import { getValues } from "@/utils/helpers";
import logger from "@/runtime-utils/observability/logger";
import { taskManagementValidation } from "@/utils/validators";
import { DialogueStyled } from "../dialogueStyled";

interface IAddTaskData {
  onClose: () => void;
  id: string | null;
}

function AddTaskData(props: IAddTaskData) {
  return <TaskData key={props.id ?? "no-task"} {...props} />;
}

function TaskData({ onClose, id }: IAddTaskData) {
  const [loading, setLoading] = React.useState(true);
  const [failed, setFailed] = React.useState(false);
  const [revision, setRevision] = React.useState(0);
  const [record, setRecord] = React.useState<TaskManagementResponse | null>(
    null
  );

  const { values, errors, touched, setValues, setFieldValue } =
    useFormik<TaskManagement>({
      initialValues: {
        selectedUsers: [],
        selectedCategory: null,
        selectedTasks: [],
        startDate: new Date(),
        startTime: "",
        idealTime: IDEAL_TIME.ANYTIME,
        dayTime: [],
        recurring: false,
        description: "",
        noteSubmitRequired: false,
        rrule: {},
      },
      validationSchema: taskManagementValidation,
      onSubmit: async (values, { setSubmitting }) => {
        try {
          const finalValues = {
            ...values,
            selectedCategory: values.selectedCategory?.value,
            selectedTasks: getValues(values.selectedTasks),
            selectedUsers: getValues(values.selectedUsers),
          };

          const response = await updateTask(id, finalValues);

          if (response.success) {
            successToast("Task updated successfully");
          }
        } catch (error) {
          logger.error("error", error);
        } finally {
          setSubmitting(false);
        }
      },
    });

  useEffect(() => {
    if (!id) return;
    let active = true;
    const fetchTask = async () => {
      try {
        const res = await getTaskById<TaskManagementResponse>(id);
        if (!active) return;
        if (!res.success) {
          setFailed(true);
          return;
        }
        setValues((previous) => ({
          ...previous,
          selectedUsers: [
            {
              label: res.data?.serviceUser?.name,
              value: res.data?.serviceUser?._id,
            },
          ],
          selectedCategory: {
            label: res.data.category.title,
            value: res.data.category._id,
          },
          selectedTasks: res.data?.tasks.map(
            (el: TaskManagementResponse["tasks"][0]) => ({
              label: el.title,
              value: el._id,
            })
          ),
          ...res.data,
        }));
        setRecord(res.data);
      } catch (error) {
        if (!active) return;
        logger.error("error", error);
        setFailed(true);
      } finally {
        if (active) setLoading(false);
      }
    };
    void fetchTask();
    return () => {
      active = false;
    };
  }, [id, revision, setValues]);

  return (
    <DialogueStyled>
      <Box className="Add-medication">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "19px",
          }}
        >
          <Typography id="modal-modal-title" variant="h3">
            View task
          </Typography>
          <Box onClick={onClose}>
            <SVG.Cross />
          </Box>
        </Box>
        <Box title="Description">
          <Box
            sx={{
              marginTop: "7%",
            }}
          >
            {loading ? (
              <DefaultLoader />
            ) : failed ? (
              <Box>
                <p role="alert">
                  We couldn’t load this task. Please try again.
                </p>
                <CostumeButton
                  onClick={() => {
                    setLoading(true);
                    setFailed(false);
                    setRevision((value) => value + 1);
                  }}
                >
                  Retry task
                </CostumeButton>
              </Box>
            ) : (
              <>
                <InfoSection
                  type="edit"
                  setFieldValue={setFieldValue}
                  values={values}
                  errors={errors}
                  touched={touched}
                  disabled={true}
                />
                <TemplateOrigin template={record?.template} />
                <AdditionalRecordedMetadata record={record} />
              </>
            )}
          </Box>
        </Box>
      </Box>
    </DialogueStyled>
  );
}

export default AddTaskData;
